import debounce from 'lodash.debounce';
import { channel } from 'redux-saga';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import { toLowerCase, replace } from 'utils/stringMutation';
import { MEDIUM_VIEWPORT_WIDTH } from 'utils/viewports';

let currentWidth = window.innerWidth;

const getBreakpoint = width => {
  switch (true) {
    case width < MEDIUM_VIEWPORT_WIDTH:
      return 'small';
    default:
      return 'large';
  }
};

// Actions
const INITIALISE_AD_MANAGER = 'valuations/ads/INITIALISE_AD_MANAGER';
const AD_MANAGER_INITIALISED = 'valuations/ads/AD_MANAGER_INITIALISED';

const REGISTER_AD = 'valuations/ads/REGISTER_AD';

const DISPLAY_ADS = 'valuations/ads/DISPLAY_ADS';
const DISPLAY_ADS_SUCCEEDED = 'valuations/ads/DISPLAY_ADS_SUCCEEDED';

const REMOVE_ADS = 'valuations/ads/REMOVE_ADS';
const REMOVE_ADS_SUCCEEDED = 'valuations/ads/REMOVE_ADS_SUCCEEDED';

const UPDATE_TARGETING = 'valuations/ads/UPDATE_TARGETING';

const ADD_RESIZE_LISTENER = 'valuations/ads/ADD_RESIZE_LISTENER';
const ADD_RESIZE_LISTENER_SUCCEEDED =
  'valuations/ads/ADD_RESIZE_LISTENER_SUCCEEDED';
const WINDOW_RESIZE_STARTED = 'valuations/ads/WINDOW_RESIZE_STARTED';
const WINDOW_RESIZE_ENDED = 'valuations/ads/WINDOW_RESIZE_ENDED';

const AD_BREAKPOINT = 'valuations/ads/AD_BREAKPOINT';
const RESET_AD_SLOTS = 'valuations/ads/RESET_AD_SLOTS';

const resizeChannel = channel();

// Selectors
export const getAdSlots = state => state.ads.slots;
export const getPageTargeting = state => state.ads.targeting;
export const getValuation = state => state.valuation.carDetails;

const initialState = {
  initialised: false,
  reset: false,
  breakpoint: getBreakpoint(currentWidth),
  targeting: {
    site: 'newwhatcar',
    p_type: 'used',
    n_sec2: 'valuation'
  },
  slots: []
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case AD_BREAKPOINT: {
      const { breakpoint } = action;

      return {
        ...state,
        breakpoint
      };
    }

    case DISPLAY_ADS: {
      return {
        ...state,
        reset: false
      };
    }

    case AD_MANAGER_INITIALISED: {
      return {
        ...state,
        initialised: true
      };
    }

    case REGISTER_AD: {
      const { slots } = state;
      const { slot } = action;
      const newSlots = slots.concat(slot);

      return {
        ...state,
        slots: newSlots
      };
    }

    case REMOVE_ADS: {
      return {
        ...state,
        slots: []
      };
    }

    case UPDATE_TARGETING: {
      const { manufacturer: manu, model: mod } = action;
      const { slots } = state;

      const manufacturer = toLowerCase(replace(manu, ' ', '-'));
      const model = toLowerCase(replace(mod, ' ', '-'));

      const slotsWithUpdatedAdUnit = slots.map(slot => {
        const { adUnit = [] } = slot;
        if (model && manufacturer) adUnit.push(manufacturer);
        if (model) adUnit.push(model);

        return {
          ...slot,
          adUnit: `/${adUnit.join('/')}`
        };
      });

      return {
        ...state,
        targeting: {
          ...state.targeting,
          p_manu: manufacturer,
          p_model: model
        },
        slots: slotsWithUpdatedAdUnit
      };
    }

    case RESET_AD_SLOTS: {
      return {
        ...state,
        reset: true
      };
    }

    default:
      return state;
  }
}

// Action Creators
export function initialiseAdManager() {
  return { type: INITIALISE_AD_MANAGER };
}

export function registerAd(slot) {
  return { type: REGISTER_AD, slot };
}

export function displayAds() {
  return { type: DISPLAY_ADS };
}

export function removeAds() {
  return { type: REMOVE_ADS };
}

// Sagas
export function* adManagerSaga() {
  yield takeEvery(INITIALISE_AD_MANAGER, handleAdManager);
}

export function* displayAdsSaga() {
  yield takeEvery(DISPLAY_ADS, handleDisplayAds);
}

export function* removeAdsSaga() {
  yield takeEvery(REMOVE_ADS, handleRemoveAds);
}

export function* addResizeListenerSaga() {
  yield takeEvery(ADD_RESIZE_LISTENER, handleAddResizeListener);
}

export function* windowResizeSaga() {
  yield takeEvery(WINDOW_RESIZE_STARTED, handleWindowResize);
}

export function* watchResizeChannel() {
  while (true) {
    const action = yield take(resizeChannel);
    yield put(action);
  }
}

export function* handleAdManager() {
  if (window.adManager) {
    yield put({ type: ADD_RESIZE_LISTENER });
    yield put({ type: AD_MANAGER_INITIALISED });
  }
}

export function* handleDisplayAds() {
  const valuation = yield select(getValuation);
  const { manufacturer, model } = valuation;

  yield put({ type: UPDATE_TARGETING, manufacturer, model });

  const targeting = yield select(getPageTargeting);
  const slots = yield select(getAdSlots);

  if (window.adManager) {
    window.adManager
      .setPageTargeting(targeting)
      .add(slots)
      .render();
  }

  yield put({ type: DISPLAY_ADS_SUCCEEDED, targeting, slots });
}

export function* handleRemoveAds() {
  if (window.adManager) {
    window.adManager.clearPageTargeting().remove();
  }

  yield put({ type: REMOVE_ADS_SUCCEEDED });
}

export function* handleAddResizeListener() {
  const DELAY_IN_MS = 500;

  window.addEventListener(
    'resize',
    debounce(() => {
      resizeChannel.put({
        type: WINDOW_RESIZE_STARTED,
        width: window.innerWidth
      });
    }, DELAY_IN_MS)
  );

  yield put({ type: ADD_RESIZE_LISTENER_SUCCEEDED });
}

export function* handleWindowResize({ width }) {
  const currentBreakPoint = yield call(getBreakpoint, currentWidth);
  const newBreakPoint = yield call(getBreakpoint, width);

  yield put({ type: AD_BREAKPOINT, breakpoint: newBreakPoint });
  yield put({ type: WINDOW_RESIZE_ENDED });

  if (currentBreakPoint !== newBreakPoint) {
    currentWidth = width;
    yield put({ type: REMOVE_ADS });
    yield put({ type: RESET_AD_SLOTS });
    yield put({ type: DISPLAY_ADS });
  }
}



// WEBPACK FOOTER //
// ./src/ducks/ads/ads.js